/*
 * Venda: required Zapiet Product Options.
 *
 * 1. Locked mode. When the catering page auto-opens Zapiet's options popup after
 *    an add (window event "venda:zapiet:lock-next"), the next popup Zapiet creates
 *    is tagged [data-options-required]: the ✕ and Cancel are hidden
 *    (snippets/custom-styles.liquid) and backdrop clicks and Escape are blocked.
 *    Opened from "Edit your order" it closes normally.
 *
 * 2. Checkout safety net. After every cart refresh, each line is checked against
 *    Zapiet's own option config (printed on the page as `shopMetafields`). If a
 *    required field that applies to the line has no saved value, checkout is
 *    blocked and .cart--options-required (snippets/cart.liquid) is shown.
 *    Optional fields never block. If the config can't be read, nothing is blocked.
 *
 * 3. Softer reload. Zapiet reloads the page after Save; if the cart drawer was
 *    open, it's reopened instantly at the same scroll position after the reload.
 */
const POPUP = 'dialog.edit-cart-options-modal';
const LOCKED = 'data-options-required';
const LOCK_TIMEOUT = 8000;

/* ---------- 1. locked mode ---------- */

let lock_timer = null;

// Zapiet builds a new popup every time it opens one, so tag the next one as it's added.
// Only watches the page while a lock is armed.
const popup_watcher = new MutationObserver(records => {
  for (const { addedNodes } of records) {
    for (const node of addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const popup = node.matches(POPUP) ? node : node.querySelector(POPUP);
      if (!popup) continue;

      popup.setAttribute(LOCKED, '');
      disarmLock();
      return;
    }
  }
});

function disarmLock() {
  clearTimeout(lock_timer);
  popup_watcher.disconnect();
}

window.addEventListener('venda:zapiet:lock-next', () => {
  disarmLock();
  popup_watcher.observe(document.body, { childList: true, subtree: true });
  lock_timer = setTimeout(disarmLock, LOCK_TIMEOUT);
});

const isLockedPopup = target => target instanceof HTMLDialogElement && target.matches(`${POPUP}[${LOCKED}]`);

// a click on the ::backdrop is delivered to the <dialog> itself; stop it before Zapiet's handler
['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(type => {
  window.addEventListener(type, event => {
    if (!isLockedPopup(event.target)) return;
    event.stopImmediatePropagation();
    event.preventDefault();
  }, true);
});

window.addEventListener('keydown', event => {
  if (event.key !== 'Escape' || !document.querySelector(`${POPUP}[${LOCKED}][open]`)) return;
  event.stopImmediatePropagation();
  event.preventDefault();
}, true);

window.addEventListener('cancel', event => {
  if (isLockedPopup(event.target)) event.preventDefault();
}, true);

/* ---------- 2. checkout safety net ---------- */

let config;
let incomplete_items = [];
let check_timer = null;

function readZapietConfig() {
  try {
    const script = [...document.scripts].find(({ src, textContent }) => !src && textContent.includes('const shopMetafields'));
    const match = script?.textContent.match(/const shopMetafields = (\{.*?\});\s*\n/s);
    const option_config = match && JSON.parse(match[1])['option-groups'];
    return option_config?.option_groups && option_config.options ? option_config : null;
  } catch {
    return null;
  }
}

function requiredLabels(item) {
  const labels = [];

  config.option_groups.forEach(group => {
    if (group.status === 0) return;

    let applies = false;
    if (group.type === 'types') {
      applies = (group.rules?.types || []).includes(item.product_type);
    } else if (group.type === 'products') {
      applies = (group.rules?.products || []).some(({ id }) => String(id) === String(item.product_id));
    }
    // any other rule type can't be checked here, so it never blocks checkout
    if (!applies) return;

    const conditional = group.conditionalLogic?.rules || {};
    (group.options || []).forEach(({ id, hidden }) => {
      const option = config.options[id];
      // conditional fields only apply when shown, so they never block either
      if (!option?.is_required || hidden || conditional[id]) return;
      labels.push(option.label.trim());
    });
  });

  return labels;
}

// saved fields are line item properties named after the label (paid ones get a price suffix)
const hasValue = (item, label) =>
  Object.entries(item.properties || {}).some(([key, value]) => key.startsWith(label) && String(value).trim() !== '');

async function checkCart() {
  config ??= readZapietConfig();
  if (!config) return;

  let cart;
  try {
    const response = await fetch(`${Shopify.routes.cart}.js`);
    if (!response.ok) return;
    cart = await response.json();
  } catch {
    return;
  }

  incomplete_items = cart.items.filter(item => requiredLabels(item).some(label => !hasValue(item, label)));
  render();
}

function scheduleCheck() {
  clearTimeout(check_timer);
  check_timer = setTimeout(checkCart, 150);
}

function render() {
  const names = [...new Set(incomplete_items.map(({ product_title }) => product_title))].join(', ');

  document.querySelectorAll('.cart--root').forEach(cart => {
    const notice = cart.querySelector('.cart--options-required');
    const text = cart.querySelector('.cart--options-required--text');
    const checkout = cart.querySelector('.cart--checkout-button');
    if (!notice || !checkout) return;

    if (incomplete_items.length) {
      text.textContent = text.dataset.template.replace('[products]', names);
      notice.hidden = false;
      checkout.setAttribute('disabled', 'disabled');
      checkout.setAttribute('data-options-blocked', '');
    } else {
      notice.hidden = true;
      if (checkout.hasAttribute('data-options-blocked')) {
        checkout.removeAttribute('disabled');
        checkout.removeAttribute('data-options-blocked');
      }
    }
  });
}

function openOptions(item) {
  const edit_button = document.querySelector(`[id="${item.variant_id}--zapiet--edit-button"]`);
  if (!edit_button) {
    window.location.href = item.url;
    return;
  }

  window.dispatchEvent(new CustomEvent('venda:zapiet:lock-next'));
  edit_button.click();
}

// the theme re-enables checkout after each refresh; this also covers the moment before the re-check
document.addEventListener('submit', event => {
  if (!event.target.matches('.cart--form') || !incomplete_items.length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  event.target.querySelector('.cart--checkout-button')?.setAttribute('aria-busy', false);
  render();
}, true);

document.addEventListener('click', event => {
  if (!event.target.closest('.cart--options-required--button') || !incomplete_items.length) return;
  event.preventDefault();
  openOptions(incomplete_items[0]);
});

window.addEventListener('theme:cart:updated', scheduleCheck);

// nothing to check on an empty cart, so don't spend a /cart.js request on most page views
if (document.querySelector('[data-cart-count]')?.getAttribute('data-cart-count') !== '0') scheduleCheck();

/* ---------- 3. softer reload after Zapiet's Save ---------- */

// Zapiet always reloads the page after Save (window.location.reload, not configurable).
// Remember where the customer was and put them back: browsers keep the page scroll on
// reload, so this reopens the cart drawer instantly at the same scroll position.
const RESTORE_KEY = 'venda:zapiet:restore';
const RESTORE_MAX_AGE = 15000;

document.addEventListener('click', event => {
  if (!event.target.closest('[name="zapiet-cart-options-save-button"]')) return;

  const viewport = document.querySelector('.layout--viewport');
  const drawer_open = viewport?.getAttribute('data-drawer-status') !== 'closed' && viewport?.getAttribute('data-drawer-view') === 'cart-drawer';
  try {
    sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
      path: location.pathname + location.search,
      drawer_open,
      drawer_scroll: document.querySelector('.cart--root[data-view="drawer"] .cart--form')?.scrollTop || 0,
      time: Date.now()
    }));
  } catch {}
}, true);

function restoreAfterSave() {
  let saved;
  try {
    saved = JSON.parse(sessionStorage.getItem(RESTORE_KEY));
    sessionStorage.removeItem(RESTORE_KEY);
  } catch {
    return;
  }
  if (!saved?.drawer_open || saved.path !== location.pathname + location.search || Date.now() - saved.time > RESTORE_MAX_AGE) return;

  // open without the slide-in so it reads as "the same page", then turn transitions back on
  const root = document.documentElement;
  const viewport = document.querySelector('.layout--viewport');
  root.setAttribute('data-instant-drawer', '');

  // the theme scrolls the drawer to the top while opening, so restore once it's open
  const finish = () => {
    status_watcher.disconnect();
    clearTimeout(fallback);
    const form = document.querySelector('.cart--root[data-view="drawer"] .cart--form');
    if (form) form.scrollTop = saved.drawer_scroll;
    root.removeAttribute('data-instant-drawer');
  };
  const status_watcher = new MutationObserver(() => {
    if (viewport.getAttribute('data-drawer-status') === 'opened') finish();
  });
  status_watcher.observe(viewport, { attributes: true, attributeFilter: ['data-drawer-status'] });
  const fallback = setTimeout(finish, 1500);

  window.dispatchEvent(new CustomEvent('theme:drawer:open', { detail: { side: 'right', view: 'cart-drawer' } }));
}

customElements.whenDefined('drawer-element').then(restoreAfterSave);
